import { defineStore } from 'pinia';
import { useAuthStore } from './authStore';
import { useDsaReportsTicketsStore } from './dsaReportsTicketsStore';
import { useDsaReportsPeriodsStore } from './dsaReportsPeriodsStore';
import { useDsaReportsProjectsStore } from './dsaReportsProjectsStore';
import { useDsaReportsMilestonesStore } from './dsaReportsMilestonesStore';
import { useDsaReportsActivitiesStore } from './dsaReportsActivitiesStore';
import {
  getCurrentUser,
  getDsaReportsCompanies,
  getDsaReportsContracts,
  getDsaReportsIssues
} from '../features/dsa-reports/api/dsaReportsApi';

const DSA_CACHE_TTL_MS = 60 * 60 * 1000;
const DSA_CONTRACTS_CACHE_KEY = 'dsaContractsCache:v2';

const mapDurationUnitToFrequency = (durationUnit) => {
  switch ((durationUnit || '').toLowerCase()) {
    case 'day':
      return 'On-Demand';
    case 'fortnight':
      return 'Fortnightly';
    case 'month':
      return 'Monthly';
    case 'quarter':
      return 'Quarterly';
    case 'year':
      return 'Yearly';
    default:
      return '';
  }
};

const readDsaCache = (key) => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.timestamp) return null;
    if (Date.now() - parsed.timestamp > DSA_CACHE_TTL_MS) return null;
    return parsed.data;
  } catch (err) {
    console.warn('[DSA] Failed to read cache', err);
    return null;
  }
};

const writeDsaCache = (key, data) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      key,
      JSON.stringify({ timestamp: Date.now(), data })
    );
  } catch (err) {
    console.warn('[DSA] Failed to write cache', err);
  }
};

export const useDsaReportsStore = defineStore({
  id: 'dsaReportsStore',
  state: () => ({
    contracts: [],
    companies: [],
    issues: [],
    affiliations: [],
    frequencies: [
      { id: 'On-Demand', value: 'On-Demand', label: 'On-Demand' },
      { id: 'Bi-Weekly', value: 'Bi-Weekly', label: 'Bi-weekly' },
      { id: 'Monthly', value: 'Monthly', label: 'Monthly' },
      { id: 'Quarterly', value: 'Quarterly', label: 'Quarterly' },
      { id: 'Yearly', value: 'Yearly', label: 'Yearly' },
      { id: 'Fortnightly', value: 'Fortnightly', label: 'Fortnightly' },
    ],
    selectedContract: '',
    selectedCompany: '',
    selectedFrequency: '',
    selectedFrequencyID: '',
    startDate: null,
    endDate: null,
    UTCStartDate: null,
    UTCEndDate: null,
    reportNotes: '',
    userId: '',
    userName: '',
    email: '',
    isLoading: false,
    lastReport: {
      company: '',
      frequency: '',
      startDate: '',
      endDate: '',
      UTCStartDate: '',
      UTCEndDate: '',
    },
  }),
  actions: {
    getDefaultFrequencyForContract(contract) {
      if (!contract) return '';
      const explicitFrequency = contract.frequency?.value || contract.frequency?.id || '';
      if (explicitFrequency) {
        return explicitFrequency;
      }
      return mapDurationUnitToFrequency(contract.periodTemplate?.durationUnit);
    },
    convertToEpoch(dateStr, { endOfDay = false } = {}) {
      const date = new Date(dateStr);

      if (Number.isNaN(date.getTime())) {
        return null;
      }

      if (endOfDay) {
        // Include the entire day by moving to the last millisecond in local time
        date.setHours(23, 59, 59, 999);
      } else {
        // Normalize to start of day in local time
        date.setHours(0, 0, 0, 0);
      }

      // getTime is already UTC-based, so we can convert directly to seconds
      return Math.floor(date.getTime() / 1000);
    },
    // Selects a contract and sets the frequency if available
    selectContract(contractId) {
      if (this.selectedContract && this.selectedContract !== contractId) {
        this.changeCompany();
      }
      this.selectedContract = contractId;
      const contract = this.contracts.find(c => c.id === contractId);
      this.selectedFrequency = this.getDefaultFrequencyForContract(contract);
    },
    // Selects a company
    selectCompany(companyId) {
      this.selectedCompany = companyId;
    },
    // Selects a frequency
    selectFrequency(frequencyId) {
      this.selectedFrequency = frequencyId;
    },
    // Updates start date and converts to UTC
    updateStartDate(startDate) {
      this.startDate = startDate;
      const startEpoch = this.convertToEpoch(startDate);
      this.UTCStartDate = startEpoch;
    },
    // Updates end date and converts to UTC
    updateEndDate(endDate) {
      this.endDate = endDate;
      const endEpoch = this.convertToEpoch(endDate, { endOfDay: true });
      this.UTCEndDate = endEpoch;
    },
    setNote(newNote) {
      this.reportNotes = newNote;
    },
    clearReportNotes() {
      this.reportNotes = '';
    },
    async fetchContracts() {
      const authStore = useAuthStore();
      if (!authStore.token) {
        //console.log('Authentication token is not available. Waiting for login.');
        return;
      }

      const cacheKey = DSA_CONTRACTS_CACHE_KEY;
      const cached = readDsaCache(cacheKey);
      if (cached) {
        this.contracts = cached;
        return;
      }

      this.isLoading = true; // Set isLoading to true before making the request

      try {
        const response = await getDsaReportsContracts();

        this.contracts = response.data;
        writeDsaCache(cacheKey, this.contracts);
      } catch (error) {
        console.error('Error fetching contracts:', error);
      } finally {
        this.isLoading = false; // Set isLoading to false after the request is complete
      }
    },

    async fetchCompanies() {
      const authStore = useAuthStore();
      if (!authStore.token) {
        throw new Error('Authentication token is not available.');
      }

      const cacheKey = 'dsaCompaniesCache';
      const cached = readDsaCache(cacheKey);
      if (cached) {
        this.companies = cached;
        return;
      }

      try {
        const response = await getDsaReportsCompanies();
        this.companies = response.data;
        writeDsaCache(cacheKey, this.companies);
      } catch (error) {
        console.error('Error fetching companies:', error);
      }
    },

    async fetchUserData() {
      const authStore = useAuthStore();
      if (!authStore.token) {
        throw new Error('Authentication token is not available.');
      }

      try {
        const response = await getCurrentUser();

        // Update store state with fetched user data
        this.userName = `${response.data.firstName} ${response.data.surname}`;
        this.email = response.data.email;
        //console.log(response);
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    },

    async fetchIssues(companyId, UTCStartDate, UTCEndDate) {
      const authStore = useAuthStore();
      if (!authStore.token || !companyId || !UTCStartDate || !UTCEndDate) {
        this.error = 'Missing required fields';
        return;
      }
      const cacheKey = `dsaIssuesCache:${companyId}:${UTCStartDate}:${UTCEndDate}`;
      const cached = readDsaCache(cacheKey);
      if (cached) {
        this.issues = cached;
        return;
      }
      this.loading = true;
      try {
        const response = await getDsaReportsIssues({
          companyId,
          startDate: UTCStartDate,
          endDate: UTCEndDate,
        });
        this.issues = response.data.response;
        writeDsaCache(cacheKey, this.issues);
      } catch (error) {
        this.error = error.message || 'Error fetching issues';
      } finally {
        this.loading = false;
      }
    },

    clearData() {
      this.contracts = [];
      this.companies = [];
      this.issues = [];
      this.frequencies = [];
      this.affiliations = [];
      this.startDate = null;
      this.endDate = null;
      this.UTCStartDate = null;
      this.UTCEndDate = null;
      this.reportNotes = '';
      this.userName = null;
      this.email = null;
    },
    changeCompany() {
      const ticketStore = useDsaReportsTicketsStore();
      const periodStore = useDsaReportsPeriodsStore();
      const activityStore = useDsaReportsActivitiesStore();
      const projectStore = useDsaReportsProjectsStore();
      const milestoneStore = useDsaReportsMilestonesStore();

      this.issues = [];
      this.selectedFrequency = '';
      this.affiliations = [];
      this.startDate = null;
      this.endDate = null;
      this.UTCStartDate = null;
      this.UTCEndDate = null;
      this.reportNotes = '';
      periodStore.setBudgetSource('allowance');
      ticketStore.clearTickets();
      periodStore.clearPeriods();
      activityStore.clearActivities();
      projectStore.clearProjects();
      milestoneStore.clearMilestones();
      
    },
    clearTickets() {
      const ticketStore = useDsaReportsTicketsStore();
      const periodStore = useDsaReportsPeriodsStore();
      const activityStore = useDsaReportsActivitiesStore();
      const projectStore = useDsaReportsProjectsStore();
      const milestoneStore = useDsaReportsMilestonesStore();

      ticketStore.clearTickets();
      periodStore.clearPeriods();
      activityStore.clearActivities();
      projectStore.clearProjects();
      milestoneStore.clearMilestones();
    },

    // Store the last report parameters if selectedContract is not empty
    setLastReportParams() {
      if (this.selectedContract) {
        this.lastReport = {
          company: this.selectedContract,
          frequency: this.selectedFrequency,
          startDate: this.startDate,
          endDate: this.endDate,
          UTCStartDate: this.UTCStartDate,
          UTCEndDate: this.UTCEndDate,
        };
      }
    },

    // Set the current values to the last report values
    setCurrentToLastReport() {
      const periodStore = useDsaReportsPeriodsStore();

      this.selectedContract = this.lastReport.company;
      this.selectedFrequency = this.lastReport.frequency;
      this.startDate = this.lastReport.startDate;
      this.endDate = this.lastReport.endDate;
      this.UTCStartDate = this.lastReport.UTCStartDate;
      this.UTCEndDate = this.lastReport.UTCEndDate;
      periodStore.fetchPeriods(this.selectCompany.id, this.UTCStartDate, this.UTCEndDate)
    }
  },
  getters: {
    // Getter for the label of the selected frequency
    selectedFrequencyLabel() {
      const frequency = this.frequencies.find((f) => f.id === this.selectedFrequency);
      return frequency ? frequency.label : '';
    },
    // Getter for the name of the selected company
    selectedContractName() {
      const contract = this.contracts.find((f) => f.id === this.selectedContract);
      return contract ? contract.company.name : '';
    },
    selectedCompanyName() {
      const company = this.companies.find((f) => f.id === this.selectedCompany);
      return company ? company.name : '';
    },
    // Getter for formatted start date
    formattedStartDate() {
      if (!this.startDate) return 'Not set';
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(this.startDate).toLocaleDateString('en-US', options);
    },
    // Getter for formatted end date
    formattedEndDate() {
      if (!this.endDate) return 'Not set';
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(this.endDate).toLocaleDateString('en-US', options);
    },
    // Check if last report is available
    lastReportAvailable() {
      return this.lastReport.company && this.lastReport.frequency && this.lastReport.startDate && this.lastReport.endDate;
    }
  },
});
